import { SelectionModel } from '@angular/cdk/collections';
import { DatePipe } from '@angular/common';
import { Component, Inject, OnInit, Optional, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogConfig, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { HotToastService, ToastConfig } from '@ngneat/hot-toast';
import { NgxSpinnerService } from 'ngx-spinner';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AccionesDialog } from '@models/base/identificadores.model';
import { CreateSettlementResponse } from '@models/propietarios/createSettlementResponse.model';
import { PersonaPropietarioDto } from '@models/propietarios/personaPropietarioDto.model';
import { LoginService } from '@services/login/login.service';
import { PersonasService } from '@services/personas/personas.service';
import { PropietariosService } from '@services/propietarios/propietarios.service';
import { SnackBarService } from '@services/snackBar/snack-bar-service.service';
import { DialogVisualizaLiquidacionComponent } from '@views/contratos/form-editar-contrato/dialog-nueva-liquidacion/dialog-visualiza-liquidacion/dialog-visualiza-liquidacion.component';
import { DialogLiquidacionesGeneradasComponent } from '@views/contratos/form-editar-contrato/dialog-nueva-liquidacion/dialog-liquidaciones-generadas/dialog-liquidaciones-generadas.component';

@Component({
  selector: 'app-dialog-generar-liquidaciones',
  templateUrl: './dialog-generar-liquidaciones.component.html',
  styleUrls: ['./dialog-generar-liquidaciones.component.css'],
  providers: [DatePipe]
})
export class DialogGenerarLiquidacionesComponent implements OnInit {

  action: any;
  data: boolean = false;
  procesando: boolean = false;
  displayedColumns: string[] = ['select', 'descripcion', 'nroDocumento'];
  dataSource = new MatTableDataSource<PersonaPropietarioDto>();
  selection = new SelectionModel<PersonaPropietarioDto>(true, []);
  tipoLiquidacion = "agrupada";

  private paginator!: MatPaginator;
  private sort!: MatSort;
  form: FormGroup;

  @ViewChild(MatSort) set matSort(ms: MatSort) {
    this.sort = ms;
    this.setDataSourceAttributes();
  }

  @ViewChild(MatPaginator) set matPaginator(mp: MatPaginator) {
    this.paginator = mp;
    this.setDataSourceAttributes();
  }

  setDataSourceAttributes() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;

    if (this.paginator && this.sort) {
      this.applyFilter(null);
    }
  }

  constructor(
    private spinner: NgxSpinnerService,
    private snackBar: SnackBarService,
    private toastService: HotToastService,
    private propietariosService: PropietariosService,
    private personasService: PersonasService,
    private loginService: LoginService,
    public dialogRef: MatDialogRef<DialogGenerarLiquidacionesComponent>,
    private dialog: MatDialog,
    private fb: FormBuilder,
    @Optional() @Inject(MAT_DIALOG_DATA) public datos: any
  ) {
    let toasConfig = new ToastConfig();
    toasConfig.autoClose = false;
    toasConfig.reverseOrder = true;
    toasConfig.position = 'bottom-right';
    this.toastService.defaultConfig = toasConfig;

    this.form = this.fb.group({
      fechaCierre: [new Date(), Validators.required]
    });
  }

  ngOnInit(): void {
    this.getAllPropietarios();
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  /** Whether the number of selected elements matches the total number of filtered rows. */
  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numFilteredRows = this.dataSource.filteredData.length;

    // Verificar que todos los elementos filtrados estén seleccionados
    if (numFilteredRows === 0) return false;

    const filteredSelected = this.selection.selected.filter(item =>
      this.dataSource.filteredData.includes(item)
    );

    return filteredSelected.length === numFilteredRows && numFilteredRows > 0;
  }

  /** Selects all filtered rows if they are not all selected; otherwise clear selection of filtered rows. */
  masterToggle() {
    if (this.dataSource.filteredData.length === 0) {
      return; // No hay datos filtrados para seleccionar
    }

    if (this.isAllSelected()) {
      // Deseleccionar solo los elementos filtrados
      this.dataSource.filteredData.forEach(row => {
        this.selection.deselect(row);
      });
    } else {
      // Seleccionar solo los elementos filtrados
      this.dataSource.filteredData.forEach(row => {
        this.selection.select(row);
      });
    }
  }

  /** The label for the checkbox on the passed row */
  checkboxLabel(row?: PersonaPropietarioDto): string {
    if (!row) {
      const filteredCount = this.dataSource.filteredData.length;
      return `${this.isAllSelected() ? 'deseleccionar' : 'seleccionar'} todos (${filteredCount} elementos)`;
    }
    return `${this.selection.isSelected(row) ? 'deseleccionar' : 'seleccionar'} fila ${row.descripcion}`;
  }

  /** Método para obtener el conteo de elementos seleccionados filtrados */
  getSelectedFilteredCount(): number {
    return this.selection.selected.filter(item =>
      this.dataSource.filteredData.includes(item)
    ).length;
  }

  /** Validación para verificar si se puede procesar la selección */
  canProcessSelection(): boolean {
    return this.getSelectedFilteredCount() > 0 && !this.procesando;
  }

  visualizaLiquidacion() {
    //  Validar que haya selección
    if (!this.canProcessSelection()) {
      this.snackBar.showWarn("Debe seleccionar al menos un propietario para visualizar las liquidaciones.", "Advertencia");
      return;
    }

    //  Validar formulario
    if (!this.form.valid) {
      this.snackBar.showWarn("Por favor, complete correctamente todos los campos requeridos.", "Advertencia");
      return;
    }

    const fecha = this.form.controls['fechaCierre'].value as Date;
    if (!fecha) {
      this.snackBar.showWarn("Debe seleccionar una fecha de cierre.", "Advertencia");
      return;
    }

    //  Validar fecha de cierre

    const datePipe = new DatePipe('en-US');
    const fechaFormateada = datePipe.transform(fecha, 'yyyy-MM-dd');

    // Filtrar propietarios seleccionados que están visibles
    let propietariosSeleccionados = this.selection.selected.filter(item =>
      this.dataSource.filteredData.includes(item)
    );

    // Validar que haya propietarios después del filtro
    if (propietariosSeleccionados.length === 0) {
      this.snackBar.showWarn("Los propietarios seleccionados no están en la vista actual. Por favor, ajuste los filtros.", "Advertencia");
      return;
    }

    //  Extraer IDs
    let idsPropietarios: number[] = propietariosSeleccionados.map(element => element.id_Propietario);

    // Validar que todos los IDs sean válidos
    if (idsPropietarios.some(id => !id || id <= 0)) {
      this.snackBar.showError("Error al obtener los IDs de los propietarios seleccionados.", "Error");
      return;
    }

    // Ejecutar proceso
    this.getSettlement(fechaFormateada, idsPropietarios);
  }

  getSettlement(date: any, idsPropietarios: number[]) {
    this.procesando = true;
    this.propietariosService.getSettlement(date, idsPropietarios, 0)
      .pipe(
        this.toastService.observe({
          loading: 'Por favor, aguarde',
          success: (s) => 'Proceso finalizado con éxito.',
          error: (e) => e,
        })
      )
      .subscribe(
        data => {
          this.toastService.close();
          if (data.settlementsByProperty.length != 0) {
            this.openDialogVisualizaLiquidacionComponent(data);
          } else {
            this.snackBar.showInfo("No se registraron datos.", "Info");
          }
          this.procesando = false;
        },
        error => {
          this.procesando = false;
          this.snackBar.showError("Error al obtener las liquidaciones.", "Error");
        }
      );
  }

  openDialogVisualizaLiquidacionComponent(settlementResponse: any) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = "100%";
    dialogConfig.autoFocus = false;
    dialogConfig.data = { settlementResponse: settlementResponse, contratoDetailDto: null };

    const dialogRef = this.dialog.open(DialogVisualizaLiquidacionComponent, dialogConfig);

    dialogRef.afterClosed().subscribe(result => {
      // Implementar lógica si es necesario después de cerrar el diálogo
    });
  }

  generarLiquidacion() {
    if (!this.canProcessSelection()) {
      this.snackBar.showWarn("Debe seleccionar al menos un propietario para generar las liquidaciones.", "Advertencia");
      return;
    }

    const fecha = this.form.controls['fechaCierre'].value as Date;
    if (!fecha) {
      this.snackBar.showWarn("Debe seleccionar una fecha de cierre.", "Advertencia");
      return;
    }

    //  Validar fecha de cierre

    this.procesando = true;
    const usuarioDto = this.loginService.getCurrentUser();
    const idsPropietarios: number[] = [];

    // Solo procesar los propietarios seleccionados que están en la vista filtrada
    const propietarios = this.selection.selected.filter(item =>
      this.dataSource.filteredData.includes(item)
    );

    const agurparPorContrato = this.tipoLiquidacion === "agrupada";

    // Recolectar los IDs de los propietarios seleccionados
    propietarios.forEach(element => {
      idsPropietarios.push(element.id_Propietario);
    });

    // Iniciar el toast de loading
    const toastId = this.toastService.loading('Por favor, aguarde', {
      icon: '⏳',
      autoClose: false
    });

    this.propietariosService.createSettlement(idsPropietarios, usuarioDto.id, 0, agurparPorContrato,fecha)
      .subscribe(
        data => {
          this.toastService.success('Proceso finalizado con éxito.');
          toastId.close();

          // Filtrar los propietarios procesados y actualizar la tabla
          this.dataSource.data = this.dataSource.data.filter(
            item => !idsPropietarios.includes(item.id_Propietario)
          );

          // Limpiar selección
          this.selection.clear();

          if (data.liquidacionPorContrato.length !== 0) {
            this.openDialogLiquidacionesGeneradasComponent(data);
          } else {
            this.toastService.info("No se registraron datos.");
          }

          this.procesando = false;
        },
        error => {
          this.toastService.error('Error al procesar la liquidación.');
          toastId.close();
          this.procesando = false;
        }
      );
  }

  openDialogLiquidacionesGeneradasComponent(createSettlementResponse: CreateSettlementResponse) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = "50%";
    dialogConfig.autoFocus = false;
    dialogConfig.data = createSettlementResponse;

    const dialogRef = this.dialog.open(DialogLiquidacionesGeneradasComponent, dialogConfig);

    dialogRef.afterClosed().subscribe(result => {
      // Implementar lógica si es necesario después de cerrar el diálogo
    });
  }

  closeDialog() {
    this.dialogRef.close({ event: AccionesDialog.Cancelar });
  }

  getAllPropietarios() {
    this.spinner.show("spPropietarios");
    this.personasService.getAllPersonasPropietarios()
      .subscribe(
        data => {
          this.spinner.hide("spPropietarios");
          this.dataSource.data = data.filter(x => x.id_Propietario != 0 && x.titular == true);
          this.data = this.dataSource.data.length >= 1 ? true : false;
        },
        error => {
          this.spinner.hide("spPropietarios");
          this.snackBar.showError(error, "Error");
        }
      );
  }

  /** Método mejorado para aplicar filtro con validaciones */
  applyFilter(event: any) {
    if (event && event.target && event.target.value !== undefined) {
      const filterValue = (event.target as HTMLInputElement).value;
      this.dataSource.filter = filterValue.trim().toLowerCase();

      // Limpiar selecciones de elementos que ya no están visibles después del filtro
      if (this.selection.selected.length > 0) {
        const filteredData = this.dataSource.filteredData;
        this.selection.selected.forEach(selectedItem => {
          if (!filteredData.includes(selectedItem)) {
            this.selection.deselect(selectedItem);
          }
        });
      }

      if (this.dataSource.paginator) {
        this.dataSource.paginator.firstPage();
      }
    }
  }
}
